import React, { lazy } from "react";
import {
  Navigate,
  redirect,
  useOutletContext,
  Outlet,
  type RouteObject,
} from "react-router-dom";
import RouteErrorBoundary from "./lib/RouteErrorBoundary";
import RequireFeature from "./components/RequireFeature";
import { useAccess } from "./hooks/useAccess";
import { useAccessToken } from "./hooks/useAuthentication";
import { NAV, landingPath } from "./nav";
import { LEGACY_REDIRECTS } from "./routeMappings";
import type { RootOutletContext } from "./lib/rootContext";

// THE route tree, as a plain array of routes — deliberately separate from the
// router that hosts it.
//
// `router.tsx` mounts this under RootLayout in the app's hash router, and the
// SPLIT view (`components/split/`) mounts the SAME array a second time under a
// memory router so the right-hand pane is a real route, with working sub-tab
// splats, <NavLink> pills, loaders and redirects. One array means the two
// panes can never disagree about what a page is or which feature gates it.

// Route-level code-splitting: each page is its own lazy chunk so the initial
// bundle doesn't ship every tab's code up front. RootLayout, the guards and the
// section layouts stay eager.
const DatabaseView = lazy(() => import("./components/Database"));
const TradeUploads = lazy(() => import("./components/trade-uploads/TradeUploads"));
const ExcelUploader = lazy(() => import("./components/ExcelUploader"));
const FundFactsLayout = lazy(() => import("./components/FundFacts"));
const GenerateView = lazy(() => import("./components/Generate"));
const FundFactsV2 = lazy(() => import("./components/fund-facts-v2/FundFactsV2"));
const AllFundsOverview = lazy(() => import("./components/fund-facts-performance/AllFundsOverview"));
const FundFactsPerformance = lazy(() => import("./components/fund-facts-performance/FundFactsPerformance"));
const ModelBuilder = lazy(() => import("./components/fund-facts-performance/ModelBuilder"));
const FundAttribution = lazy(() => import("./components/fund-attribution/FundAttribution"));
const TaxDashboard = lazy(() => import("./components/tax-dashboard/TaxDashboard"));
const EtfMetadata = lazy(() => import("./components/etf-metadata/EtfMetadata"));
const FundAdmin = lazy(() => import("./components/fund-admin/FundAdmin"));
const SecuritiesLending = lazy(() => import("./components/securities-lending/SecuritiesLending"));
const DistributionYields = lazy(() => import("./components/fund-yield/DistributionYields"));
const RiskRatings = lazy(() => import("./components/risk-ratings/RiskRatings"));
const VpcApiInterface = lazy(() => import("./components/VpcApiInterface"));
const ScheduledTasks = lazy(() => import("./components/ScheduledTasks"));
const Automations = lazy(() => import("./components/Automations"));
const SftpLogger = lazy(() => import("./components/SftpLogger"));
const MasterRecon = lazy(() => import("./components/MasterRecon"));
const PositionRecon = lazy(() => import("./components/PositionRecon"));
// Accounts — firm-wide client/account lookup over the two NBIN custody books,
// now also home to the Rebalance Calculator (was the top-level "Digit" tab).
// Owns its sub-tabs internally off the /accounts/<tab> splat segment.
const AccountsWorkspace = lazy(() => import("./components/accounts/AccountsWorkspace"));
const TradingDeskLayout = lazy(() => import("./components/TradingDesk"));
const FundCommandCenter = lazy(() => import("./components/fund-command-center/FundCommandCenter"));
const IronDome = lazy(() => import("./components/Health"));
const CashMonitor = lazy(() => import("./components/CashMonitor"));
const TradeBuildingTool = lazy(() => import("./components/TradeBuildingTool"));
const PartnerTrades = lazy(() => import("./components/partner-trades/PartnerTrades"));
const BloombergQuery = lazy(() => import("./components/BloombergQuery"));
const MoneyMovements = lazy(() => import("./components/MoneyMovements"));
// Margin Reports is rendered INSIDE Iron Dome (Health.tsx) as a sub-tab.
const ShadowNavScreen = lazy(() => import("./components/iron-dome/ShadowNavScreen"));
const FundservHoldings = lazy(() => import("./components/FundservHoldings"));
const PrivateStatements = lazy(() => import("./components/private-statements/PrivateStatements"));
const PowerBIReports = lazy(() => import("./components/PowerBIReports"));
const FundView = lazy(() => import("./components/fund-view/FundView"));
const IBKRWorkspace = lazy(() => import("./components/ibkr/IBKRWorkspace"));
const BlotterGenerator = lazy(() => import("./components/blotter-generator/BlotterGenerator"));
const TradingStats = lazy(() => import("./components/trading-stats/TradingStats"));
const SgggWorkspace = lazy(() => import("./components/sggg/SgggWorkspace"));
const SgggBooksWorkspace = lazy(() => import("./components/sggg-books/SgggBooksWorkspace"));
const PerpetualHedging = lazy(() => import("./components/PerpetualHedging"));
const AdminLayout = lazy(() => import("./components/admin/AdminLayout"));
const AccessAdmin = lazy(() => import("./components/admin/AccessAdmin"));
const FinanceLayout = lazy(() => import("./components/finance/FinanceLayout"));
const RevenueForecast = lazy(() => import("./components/finance/RevenueForecast"));
const FeeCalc = lazy(() => import("./components/finance/FeeCalcTab"));
// The Fees app: full-screen (RootLayout hides the IFM header on /fees), same grant as Fee Calc.
const FeeApp = lazy(() => import("./components/fee-app/FeeApp"));

// ── Access wrappers ───────────────────────────────────────────────────────────
// Every section and page is wrapped in <RequireFeature/> with the SAME feature
// key the backend gates that surface with (src/nav.ts ↔ app.py's guard table).
// A section is open to anyone who can open at least one of its pages.

const sectionFeatures = (key: string): string[] => {
  const s = NAV.find((n) => n.key === key);
  if (!s) return [key];
  const feats = s.groups.flatMap((g) => g.items.filter((i) => !i.requiresCheckpoint).map((i) => i.feature));
  return feats.length ? [s.feature, ...feats] : [s.feature];
};

const gate = (feature: string | string[], label: string, el: React.ReactNode) => (
  <RequireFeature feature={feature} label={label}>{el}</RequireFeature>
);

// Shadow NAV moved out of Iron Dome into Fund Facts › Performance. The screen
// still takes the bearer as a prop (its upload cards pass it explicitly), so a
// thin page resolves it the way Health.tsx did — and, like Health.tsx, waits
// for the token outside the local bypass so the first fetch is not a 401.
const ShadowNavPage: React.FC = () => {
  const { accessToken } = useAccessToken();
  const bypassAuth = import.meta.env.VITE_BYPASS_AUTH === "true";
  if (!bypassAuth && !accessToken) return null;
  return (
    <div className="mx-auto max-w-[1400px] px-4 py-6">
      <ShadowNavScreen accessToken={accessToken} />
    </div>
  );
};

// The signed-in person's front door: the first page their groups can open.
const LandingRedirect: React.FC = () => {
  const { can } = useAccess();
  return <Navigate to={landingPath(can)} replace />;
};

// A section's index: its first page the person can open (so a Compliance user
// landing on Trading Desk is not bounced to a pill they cannot see).
const SectionIndex: React.FC<{ sectionKey: string; fallback: string }> = ({ sectionKey, fallback }) => {
  const { can } = useAccess();
  const s = NAV.find((n) => n.key === sectionKey);
  const first = s?.groups.flatMap((g) => g.items).find((i) => !i.requiresCheckpoint && can(i.feature));
  return <Navigate to={first?.to ?? fallback} replace />;
};

// Checkpoint reads the checkpoint-access state RootLayout ALREADY resolved,
// via the Outlet context forwarded through SectionLayout — a SINGLE source of
// truth shared with the Header. It must NOT call useCheckpointAccess() again:
// a second, independent instance re-fetched /vpc-api/checkpoint-access on
// every navigation into Checkpoint and failed CLOSED on any transient non-2xx,
// stranding entitled users on Access Denied with the pill lit.
const CheckpointRoute: React.FC = () => {
  const { hasCheckpointAccess, checkCheckpointAccess } = useOutletContext<RootOutletContext>();
  return (
    <VpcApiInterface
      hasCheckpointAccess={hasCheckpointAccess}
      checkpointActions={[]}
      checkCheckpointAccess={checkCheckpointAccess}
    />
  );
};

// Pathless layout that (a) hosts the section errorElement so a tab crash is
// contained to the Outlet area (Header/nav stay alive), and (b) forwards
// RootLayout's Outlet context down to the section routes.
export const SectionLayout: React.FC = () => {
  const ctx = useOutletContext<RootOutletContext>();
  return <Outlet context={ctx} />;
};

const legacyRedirectRoutes = Object.entries(LEGACY_REDIRECTS).map(([slug, to]) => ({
  path: slug,
  loader: () => redirect(to),
}));

const TD = "trading-desk";
const FF = "fund-facts";

// Every page of the app, as the children of the pathless SectionLayout route.
export const sectionRoutes: RouteObject[] = [
  { index: true, element: <LandingRedirect /> },

  {
    path: "trading-desk",
    element: gate(sectionFeatures(TD), "Trading Desk", <TradingDeskLayout />),
    children: [
      { index: true, element: <SectionIndex sectionKey={TD} fallback="/trading-desk/iron-dome" /> },
      { path: "command-center", element: gate(`${TD}.command-center`, "Command Center", <FundCommandCenter />) },
      // splat (`/*`) so a sub-tab change stays on the same route (no
      // remount); the pill component reads the sub segment.
      { path: "iron-dome/*", element: gate(`${TD}.iron-dome`, "Iron Dome", <IronDome />) },
      { path: "cash/*", element: gate(`${TD}.cash`, "Cash Monitor", <CashMonitor />) },
      { path: "trade/*", element: gate(`${TD}.trade`, "Trade Builder", <TradeBuildingTool />) },
      { path: "partner-trades/*", element: gate(`${TD}.partner-trades`, "Partner Trades", <PartnerTrades />) },
      { path: "blotter", element: gate(`${TD}.blotter`, "Trade Allocations", <BlotterGenerator />) },
      { path: "trading-stats/*", element: gate(`${TD}.trading-stats`, "Trading Stats", <TradingStats />) },
      { path: "money-movements/*", element: gate(`${TD}.money-movements`, "Money Movement", <MoneyMovements />) },
      // Perpetual Hedging (PR #189): SPX put-overlay backtester. Research tool,
      // read-only, owns no sub-tabs -- a plain gated leaf.
      { path: "perpetual-hedging", element: gate(`${TD}.perpetual-hedging`, "Perpetual Hedging", <PerpetualHedging />) },
      // Margin Reports: /trading-desk/iron-dome/margin-reports (an Iron Dome sub-tab).
      // Reconciliation (moved in from the top level).
      {
        path: "master-recon",
        children: [
          { index: true, loader: () => redirect("/trading-desk/master-recon/recon") },
          // Hybrid: MasterRecon keeps its shared recon state; the :view
          // segment (recon | mappings) drives which body it shows.
          { path: ":view", element: gate(`${TD}.master-recon`, "Master Recon", <MasterRecon />) },
        ],
      },
      { path: "position-recon", element: gate(`${TD}.position-recon`, "Position Recon", <PositionRecon />) },
      { path: "fundserv-holdings/*", element: gate(`${TD}.fundserv-holdings`, "Fundserv Holdings", <FundservHoldings />) },
      { path: "private-statements", element: gate(`${TD}.private-statements`, "Private Statements", <PrivateStatements />) },
      // Custodians & data — each owns its sub-tabs internally, so plain leaves.
      { path: "bloomberg", element: gate(`${TD}.bloomberg`, "Bloomberg", <BloombergQuery />) },
      { path: "ibkr", element: gate(`${TD}.ibkr`, "IBKR", <IBKRWorkspace />) },
      { path: "sggg", element: gate(`${TD}.sggg`, "SGGG", <SgggWorkspace />) },
      { path: "sggg-books", element: gate(`${TD}.sggg-books`, "SGGG Books", <SgggBooksWorkspace />) },
    ],
  },

  {
    path: "fund-facts",
    element: gate(sectionFeatures(FF), "Fund Facts", <FundFactsLayout />),
    children: [
      { index: true, element: <SectionIndex sectionKey={FF} fallback="/fund-facts/generate" /> },
      { path: "generate", element: gate(`${FF}.generate`, "Fund Facts", <GenerateView />) },
      { path: "v2", element: gate(`${FF}.v2`, "Fund Facts V2", <FundFactsV2 />) },
      { path: "performance", element: gate(`${FF}.performance`, "Performance", <AllFundsOverview />) },
      { path: "performance-charts", element: gate(`${FF}.performance-charts`, "Performance Charts", <FundFactsPerformance />) },
      { path: "models", element: gate(`${FF}.models`, "Models", <ModelBuilder />) },
      { path: "risk-ratings", element: gate(`${FF}.risk-ratings`, "Risk Ratings", <RiskRatings />) },
      { path: "attribution", element: gate(`${FF}.attribution`, "Attribution", <FundAttribution />) },
      { path: "shadow-nav", element: gate(`${FF}.shadow-nav`, "Shadow NAV", <ShadowNavPage />) },
      { path: "etf-metadata", element: gate(`${FF}.etf-metadata`, "ETF Metadata", <EtfMetadata />) },
      // Fund Admin: /admin/fund-admin.
      { path: "securities-lending", element: gate(`${FF}.securities-lending`, "Securities Lending", <SecuritiesLending />) },
      { path: "distribution-yields", element: gate(`${FF}.distribution-yields`, "Distribution Yields", <DistributionYields />) },
      { path: "tax-dashboard", element: gate(`${FF}.tax-dashboard`, "Tax Dashboard", <TaxDashboard />) },
    ],
  },

  // Splat so a sub-tab change stays on the same route (no remount, so
  // the bootstrap fetch and the picked account survive a pill click).
  // Per-tab gating (accounts.account, accounts.rebalance, …) happens
  // inside the workspace, which reads the same manifest.
  { path: "accounts/*", element: gate(sectionFeatures("accounts"), "Accounts", <AccountsWorkspace />) },

  {
    path: "finance",
    element: gate(sectionFeatures("finance"), "Finance", <FinanceLayout />),
    children: [
      { index: true, element: <SectionIndex sectionKey="finance" fallback="/finance/revenue" /> },
      { path: "revenue", element: gate("finance.revenue", "Revenue Forecast", <RevenueForecast />) },
      // Splat so Fee Calc's nine views are real URLs (/finance/fees/compare)
      // rather than component state: they are deep-linkable, survive a reload,
      // answer the back button, and can be opened in the split view. It was an
      // Accounts tab until 2026-09-23, keyed `accounts.fees`.
      { path: "fees/*", element: gate("finance.fees", "Fee Calc", <FeeCalc />) },
      // The nav entry for the full-screen Fees app; the app itself lives at /fees.
      { path: "fee-app", loader: () => redirect("/fees") },
    ],
  },

  // The fee engine as a standalone app (docs/features/fee-app.md). Its own
  // full-screen shell; RootLayout leaves the IFM header off this path.
  { path: "fees/*", element: gate("finance.fees", "Fees", <FeeApp />) },

  { path: "power-bi", element: gate("power-bi", "Power BI", <PowerBIReports />) },

  { path: "fund-view", element: gate("fund-view", "Fund View", <FundView />) },

  {
    path: "admin",
    element: gate(sectionFeatures("admin"), "Admin", <AdminLayout />),
    children: [
      { index: true, element: <SectionIndex sectionKey="admin" fallback="/admin/uploads/trades" /> },
      { path: "uploads", loader: () => redirect("/admin/uploads/trades") },
      { path: "uploads/trades", element: gate("admin.uploads", "Trade Uploads", <TradeUploads />) },
      { path: "uploads/excel", element: gate("admin.uploads", "Excel Upload", <ExcelUploader />) },
      { path: "fund-admin", element: gate("admin.fund-admin", "Fund Admin", <FundAdmin />) },
      { path: "database", element: gate("admin.database", "Database", <DatabaseView />) },
      { path: "tasks", element: gate("admin.tasks", "Tasks", <ScheduledTasks />) },
      { path: "automations", element: gate("admin.automations", "Automations", <Automations />) },
      { path: "sftp-logs", element: gate("admin.sftp-logs", "SFTP Logs", <SftpLogger />) },
      { path: "access", element: gate("admin.access", "Users & Groups", <AccessAdmin />) },
    ],
  },

  {
    // Checkpoint keeps its own allow-list (toolBox.checkpointAccess);
    // SectionLayout forwards RootLayout's Outlet context down to the
    // :tab child so CheckpointRoute can read the single resolved value.
    path: "checkpoint",
    element: <SectionLayout />,
    children: [
      { index: true, loader: () => redirect("/checkpoint/procedures") },
      { path: ":tab", element: <CheckpointRoute /> },
    ],
  },

  // Compliance is an Accounts page now (it was a top-level tab, then an
  // Iron Dome sub-pill). Both older homes redirect — the Iron Dome one
  // via LEGACY_REDIRECTS, which outranks the iron-dome/* splat because
  // its three static segments score higher than two plus a splat.
  { path: "compliance", loader: () => redirect("/accounts/compliance") },
  { path: "compliance/*", loader: () => redirect("/accounts/compliance") },

  // Legacy flat-hash bookmarks, Iron Dome tile deep-links, and the
  // pre-2026-09 top-level paths (uploads, database, digit, …).
  ...legacyRedirectRoutes,

  // Unknown hash → the person's front door.
  { path: "*", element: <LandingRedirect /> },
];

// The pathless layout route that wraps every page: one errorElement so a tab
// crash is contained, and the Outlet context forwarded down. Both routers
// build their tree around this.
export const sectionLayoutRoute: RouteObject = {
  element: <SectionLayout />,
  errorElement: <RouteErrorBoundary />,
  children: sectionRoutes,
};
